// =====================================================================
// src/modulos/fnrh.js — Ficha Nacional de Registro de Hóspedes (0062)
//
// O QUE O HUGO DECIDIU (25/09/2026), e que esta tela obedece:
//   · 1A — a ficha completa DENTRO do sistema: tela, fichas incompletas,
//     PDF e planilha. Sem envio ao MTur.
//   · 3 — "garvey é condomínio, mas queremos utilizar a ficha como padrão".
//   · gênero (coluna `sexo`): Feminino · Masculino · Outro · Não informar.
//   · 0064: raça/cor (IBGE + não informar), opcional, só com o módulo contratado.
//   · legalmente incapaz: marcam o hóspede (link) e a recepção (balcão).
//
// ESTA TELA SÓ LÊ. Quem preenche a ficha é o hóspede (link do pré-check-in),
// a recepção no balcão (Check-in) e o modal de Hóspedes — três lugares que
// já gravam pela `salvar_hospede`/`gravar_precheckin`. Um quarto formulário
// aqui seria mais um lugar para a regra divergir.
//
// DE ONDE VEIO O DESENHO
// O Monteiro não tem FNRH (é de flats). A tela é montada com o que este app
// já copiou de lá: os números do topo são o `statCard` do dashboard (1937);
// o filtro De/Até com atalhos é o da Auditoria; a lista é `table.table-cards`
// com `data-label` (`renderGaragem`, 8853); o PDF e a planilha são os de
// `src/lib/exportar.js` (`exportarHospedesPDF`/`Excel`).
//
// QUEM VÊ O QUÊ NÃO ESTÁ AQUI: está na `fnrh_do_periodo` (0062).
//
// 0071 — ENVIOS À FNRH. Com o envio ligado (módulo contratado E chave
// cadastrada pelo Hugo no terminal), a tela ganha a fila: o que foi, o que
// falhou e o que ficou sem CPF. "Enviar pendentes" chama a Edge Function
// `fnrh`; "Reenviar" (só erro/sem CPF, só quem edita o Check-in) devolve o
// item à fila em nome de quem clicou — o CPF que vai é o dele. A regra é
// do banco (`fila_fnrh`, `fnrh_reenviar`); a tela só mostra e chama.
// =====================================================================
import { el, icone, abrirModal, fecharModal, aviso, enquantoAtualiza } from '../ui.js';
import { hojeDe } from '../lib/contexto.js';
import { periodoPadrao } from '../lib/auditoria.js';
import { paraPdf, paraPlanilha, temPdf, temPlanilha, nomeDoArquivo } from '../lib/exportar.js';
import { rotuloSexo } from '../lib/precheckin.js';
import * as F from '../lib/fnrh.js';

const ATALHOS = [[1, 'Hoje'], [7, '7 dias'], [30, '30 dias']];

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  const sistema = !!(contexto.usuario && contexto.usuario.sistema);
  let clienteSel = sistema ? null : contexto.cliente.id;
  let clientes = [];
  const pc = () => (sistema ? { p_cliente: clienteSel } : {});
  const hoje = () => hojeDe(contexto, agora());
  let vivo = true;
  let linhas = null;
  let coleta = false;   // 0065: módulo FNRH contratado — muda a régua da ficha completa
  let dias = 7;
  let { de, ate } = periodoPadrao(hoje(), dias);
  const filtro = { texto: '', soIncompletas: false };
  // 0071
  const podeEnviar = (contexto.modulos.find(m => m.chave === 'checkin') || {}).nivel === 'editar';
  let ligado = false, envios = [], comEnviados = false, enviandoAgora = false;

  const cli = () => (sistema ? clientes.find(c => c.id === clienteSel) : contexto.cliente) || {};
  const hotel = () => cli().nome || 'Hotel';
  const slug = () => cli().slug || 'hotel';
  const fuso = () => cli().fuso || 'America/Sao_Paulo';

  async function carregar() {
    if (sistema) { clientes = await auth.ler('clientes', 'select=id,slug,nome,fuso&order=nome'); if (!clienteSel && clientes.length) clienteSel = clientes[0].id; }
    if (!clienteSel) { linhas = []; return; }
    [linhas, coleta, ligado] = await Promise.all([
      auth.rpc('fnrh_do_periodo', { p_de: de, p_ate: ate, ...pc() }).then(r => r || []),
      auth.rpc('coleta_raca', { ...pc() }).then(Boolean, () => false),
      auth.rpc('fnrh_envio_ligado', { ...pc() }).then(Boolean, () => false),
    ]);
    await carregarEnvios();
  }
  // a fila só existe com o envio ligado — ou se sobrou algo de quando estava
  async function carregarEnvios() {
    envios = await auth.rpc('fila_fnrh', { p_so_abertos: !comEnviados, ...pc() }).then(r => r || [], () => []);
  }
  function refazer() {
    return enquantoAtualiza(async () => {
      try { await carregar(); } catch (e) { linhas = []; aviso(e.message, 'erro'); }
      desenhar();
    });
  }

  // `statCard` (1937)
  const statCard = (rotulo, val, cor, id) => el('div', { class: 'stat-card', id },
    el('div', { style: { fontSize: '20px', fontWeight: '700', color: cor } }, String(val)),
    el('div', { class: 'stat-label' }, rotulo));

  function filtros() {
    // recusado, os campos voltam ao que a tela mostra (como na Auditoria)
    const aplicar = (qual, v) => {
      const nDe = qual === 'de' ? v : de, nAte = qual === 'ate' ? v : ate;
      if (!nDe || !nAte || nAte < nDe) { aviso('Período inválido: o "Até" não pode vir antes do "De".', 'erro'); desenhar(); return; }
      de = nDe; ate = nAte; dias = 0; refazer();
    };
    const atalho = n => { dias = n; ({ de, ate } = periodoPadrao(hoje(), n)); refazer(); };
    return el('div', { class: 'usu-filtros', id: 'fn-filtros' },
      sistema ? el('div', { class: 'form-row', style: { margin: 0 } }, el('label', { class: 'label', for: 'fn-cliente' }, 'Hotel'),
        el('select', { class: 'input', id: 'fn-cliente', style: { width: 'auto' }, onchange: ev => { clienteSel = ev.target.value; refazer(); } },
          clientes.map(c => el('option', { value: c.id, selected: c.id === clienteSel }, c.nome)))) : null,
      el('div', { class: 'form-row', style: { margin: 0 } }, el('label', { class: 'label', for: 'fn-de' }, 'Check-in de'),
        el('input', { type: 'date', class: 'input', id: 'fn-de', value: de, max: ate, onchange: ev => aplicar('de', ev.target.value) })),
      el('div', { class: 'form-row', style: { margin: 0 } }, el('label', { class: 'label', for: 'fn-ate' }, 'Até'),
        el('input', { type: 'date', class: 'input', id: 'fn-ate', value: ate, min: de, onchange: ev => aplicar('ate', ev.target.value) })),
      el('div', { class: 'tabs-row', style: { marginBottom: '0', alignItems: 'flex-end' }, id: 'fn-atalhos' },
        ATALHOS.map(([n, r]) => el('button', { type: 'button', class: 'btn btn-sm ' + (dias === n ? 'btn-primary' : 'btn-secondary'), dataset: { atalho: String(n) }, onclick: () => atalho(n) }, r))));
  }

  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = 'fnrh';
    cont.appendChild(filtros());
    if (linhas === null) { cont.appendChild(el('p', { class: 'vazio', id: 'fn-carregando' }, 'Carregando…')); return; }
    const r = F.resumo(linhas, coleta);
    cont.appendChild(el('div', { class: 'contadores', id: 'fn-resumo' },
      statCard('hóspedes', r.hospedes, '#1B3A7A', 'fn-hospedes'),
      statCard('fichas completas', r.completas, '#059669', 'fn-completas'),
      statCard('fichas incompletas', r.incompletas, r.incompletas ? '#dc2626' : '#6b7280', 'fn-incompletas'),
      statCard('menores', r.menores, r.menores ? '#d97706' : '#6b7280', 'fn-menores'),
      statCard('legalmente incapazes', r.incapazes, r.incapazes ? '#d97706' : '#6b7280', 'fn-incapazes')));

    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('div', { class: 'usu-filtros', style: { marginBottom: '0' } },
        el('input', { type: 'search', class: 'input', id: 'fn-filtro', placeholder: 'Quarto, nome ou documento', value: filtro.texto, style: { maxWidth: '280px' },
          oninput: ev => { filtro.texto = ev.target.value; desenharLista(); } }),
        el('label', { class: 'label', style: { display: 'flex', alignItems: 'center', gap: '6px', margin: '0', cursor: 'pointer' } },
          el('input', { type: 'checkbox', id: 'fn-so-incompletas', checked: filtro.soIncompletas, onchange: ev => { filtro.soIncompletas = ev.target.checked; desenharLista(); } }),
          'Só fichas incompletas')),
      el('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap' } },
        temPdf() && linhas.length ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'fn-pdf', onclick: baixarPdf }, icone('ic-baixar'), 'PDF') : null,
        temPlanilha() && linhas.length ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'fn-planilha', onclick: baixarPlanilha }, icone('ic-baixar'), 'Planilha') : null)));
    cont.appendChild(el('div', { id: 'fn-lista' }));
    desenharLista();
    cont.appendChild(el('div', { id: 'fn-envios' }));
    desenharEnvios();
  }

  // ── 0071: os envios à FNRH ─────────────────────────────────────────
  function desenharEnvios() {
    const lugar = cont.querySelector('#fn-envios'); if (!lugar) return;
    if (!ligado && !envios.length) { lugar.replaceChildren(); return; }
    const abertos = envios.filter(e => e.situacao !== 'enviado' && e.situacao !== 'cancelado').length;
    lugar.replaceChildren(el('div', { class: 'card', style: { marginTop: '16px' } },
      el('div', { class: 'gov-cabeca' },
        el('div', null,
          el('div', { style: { fontSize: '15px', fontWeight: '700', color: '#1B3A7A' } }, 'Envios à FNRH'),
          el('div', { class: 'usu-email', id: 'fn-envios-estado' }, ligado
            ? (abertos ? `${abertos} aguardando ou com problema` : 'Nada pendente.')
            : 'O envio está desligado neste hotel.')),
        el('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' } },
          el('label', { class: 'label', style: { display: 'flex', alignItems: 'center', gap: '6px', margin: '0', cursor: 'pointer' } },
            el('input', { type: 'checkbox', id: 'fn-com-enviados', checked: comEnviados,
              onchange: ev => { comEnviados = ev.target.checked; enquantoAtualiza(async () => { await carregarEnvios(); desenharEnvios(); }); } }),
            'Mostrar enviados'),
          ligado && podeEnviar ? el('button', { type: 'button', class: 'btn btn-primary btn-sm', id: 'fn-enviar', disabled: enviandoAgora, onclick: enviarPendentes },
            enviandoAgora ? 'Enviando…' : 'Enviar pendentes') : null)),
      envios.length
        ? el('div', { style: { overflowX: 'auto' } }, el('table', { class: 'table-cards' },
          el('thead', null, el('tr', null, ['Quarto', 'Check-in', 'Envio', 'Hóspede', 'Situação', 'Tentativas', ''].map(t => el('th', null, t)))),
          el('tbody', null, envios.map(linhaEnvio))))
        : el('p', { class: 'vazio', id: 'fn-envios-vazio' }, comEnviados ? 'Nenhum envio ainda.' : 'Nenhum envio pendente.')));
  }

  function linhaEnvio(e) {
    const s = F.situacaoDoEnvio(e.situacao);
    const expl = F.explicacaoDoEnvio(e);
    // 0093: a divergência não é envio (não tem id): a linha leva a reserva
    return el('tr', { dataset: { envio: e.envio_id || '', reserva: e.reserva_id || '', situacao: e.situacao } },
      el('td', { 'data-label': 'Quarto', style: { fontWeight: '700' } }, e.quarto || ''),
      el('td', { 'data-label': 'Check-in', style: { fontVariantNumeric: 'tabular-nums' } }, F.linhasDaLista([{ check_in: e.check_in }])[0][1]),
      el('td', { 'data-label': 'Envio' }, F.rotuloEvento(e.evento)),
      el('td', { 'data-label': 'Hóspede' }, el('span', { class: 'usu-nome' }, e.hospede || ''), e.solicitante ? el('div', { class: 'usu-email' }, `por ${e.solicitante}`) : null),
      el('td', { 'data-label': 'Situação' }, el('span', { class: 'badge ' + s.classe }, s.rotulo), expl ? el('div', { class: 'usu-email', dataset: { explica: e.envio_id || e.reserva_id || '' } }, expl) : null),
      el('td', { 'data-label': 'Tentativas', style: { fontVariantNumeric: 'tabular-nums' } }, String(e.tentativas || 0)),
      el('td', null, F.podeReenviar(e, podeEnviar)
        ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { reenviar: e.envio_id }, onclick: () => reenviar(e) }, 'Reenviar') : null));
  }

  function disparar() {
    return auth.funcao('fnrh', { acao: 'enviar', ...(sistema ? { cliente_id: clienteSel } : {}) });
  }
  async function enviarPendentes() {
    enviandoAgora = true; desenharEnvios();
    try {
      const r = await disparar();
      const m = F.avisoDoDisparo(r);
      aviso(m || (r && r.ligado ? `${r.enviados || 0} enviado(s) à FNRH.` : 'O envio à FNRH está desligado neste hotel.'), m ? 'erro' : undefined);
    } catch (e) { aviso(`Envio à FNRH: ${e.message}`, 'erro'); }
    enviandoAgora = false;
    await enquantoAtualiza(async () => { await carregarEnvios(); if (vivo) desenharEnvios(); });
  }
  async function reenviar(e) {
    try { await auth.rpc('fnrh_reenviar', { p_envio: e.envio_id, ...pc() }); } catch (x) { aviso(x.message, 'erro'); return; }
    await enviarPendentes();
  }

  function desenharLista() {
    const lugar = cont.querySelector('#fn-lista'); if (!lugar) return;
    const vis = F.filtrar(linhas, { ...filtro, coleta });
    if (!vis.length) {
      lugar.replaceChildren(el('div', { class: 'card' }, el('p', { class: 'vazio', id: 'fn-vazio' },
        !linhas.length ? 'Nenhum hóspede com check-in neste período.'
          : filtro.soIncompletas && !filtro.texto ? 'Todas as fichas deste período estão completas.' : 'Nada com esse filtro.')));
      return;
    }
    lugar.replaceChildren(el('div', { class: 'card', style: { padding: '0', overflowX: 'auto' } },
      el('table', { class: 'table-cards' },
        el('thead', null, el('tr', null, ['Quarto', 'Check-in', 'Hóspede', 'Documento', 'Gênero', 'Nacionalidade', 'Ficha'].map(t => el('th', null, t)))),
        el('tbody', null, vis.map(linha)))));
  }

  function linha(l) {
    const falta = F.faltas(l, coleta);
    return el('tr', { class: 'linha-clicavel', tabindex: '0', dataset: { hospede: l.hospede_id, completa: String(F.completa(l, coleta)) },
      onclick: () => abrirFicha(l), onkeydown: ev => { if (ev.key === 'Enter') abrirFicha(l); } },
      el('td', { 'data-label': 'Quarto', style: { fontWeight: '700' } }, l.quarto || ''),
      el('td', { 'data-label': 'Check-in', style: { fontVariantNumeric: 'tabular-nums' } }, F.linhasDaLista([l])[0][1]),
      el('td', { 'data-label': 'Hóspede' },
        el('span', { class: 'usu-nome' }, l.nome || ''),
        l.principal ? el('span', { class: 'badge badge-blue', style: { marginLeft: '6px' } }, 'titular') : null,
        l.menor ? el('span', { class: 'badge badge-orange', style: { marginLeft: '6px' } }, 'menor') : null,
        l.legalmente_incapaz ? el('span', { class: 'badge badge-orange', style: { marginLeft: '6px' } }, 'incapaz') : null,
        l.nome_social ? el('div', { class: 'usu-email' }, `nome social: ${l.nome_social}`) : null),
      el('td', { 'data-label': 'Documento' }, F.semDocumento(l) ? el('span', { class: 'badge badge-red' }, 'sem documento') : F.linhasDaLista([l])[0][3]),
      el('td', { 'data-label': 'Gênero' }, rotuloSexo(l.sexo)),
      el('td', { 'data-label': 'Nacionalidade' }, F.linhasDaLista([l])[0][6]),
      el('td', { 'data-label': 'Ficha' }, F.completa(l, coleta)
        ? el('span', { class: 'badge badge-green' }, 'completa')
        : el('span', { class: 'badge badge-orange', title: 'Falta: ' + falta.join(', '), dataset: { faltam: String(falta.length) } }, `faltam ${falta.length}`)));
  }

  // ── a ficha de uma pessoa ─────────────────────────────────────────
  function abrirFicha(l) {
    const blocos = F.fichaDePessoa(l, hoje(), fuso());
    const falta = F.faltas(l, coleta);
    abrirModal(el('div', { id: 'fn-ficha', dataset: { hospede: l.hospede_id } },
      el('h2', { class: 'modal-title' }, `Ficha de ${l.nome || ''} · quarto ${l.quarto || ''}`),
      falta.length
        ? el('div', { class: 'alert alert-error', id: 'fn-falta' }, `Falta na ficha: ${falta.join(', ')}. Complete em Hóspedes ou no Check-in.`)
        : el('div', { class: 'alert alert-success', id: 'fn-ok' }, 'Ficha completa.'),
      blocos.map(([titulo, pares]) => el('div', { style: { marginBottom: '12px' } },
        el('div', { style: { fontSize: '13px', fontWeight: '700', color: '#374151', margin: '10px 0 6px' } }, titulo),
        el('div', { style: { display: 'grid', gridTemplateColumns: 'minmax(140px, 1fr) 2fr', gap: '4px 12px', fontSize: '14px' } },
          pares.flatMap(([k, v]) => [el('div', { class: 'usu-email' }, k), el('div', { dataset: { campo: k } }, v)])))),
      el('div', { style: { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' } },
        temPdf() ? el('button', { type: 'button', class: 'btn btn-secondary', id: 'fn-ficha-pdf', onclick: () => pdfDaFicha(l) }, icone('ic-baixar'), 'PDF desta ficha') : null,
        el('button', { type: 'button', class: 'btn btn-primary', onclick: fecharModal }, 'Fechar'))));
  }

  // ── exportar ───────────────────────────────────────────────────────
  const periodoTxt = () => `check-in de ${F.linhasDaLista([{ check_in: de }])[0][1]} a ${F.linhasDaLista([{ check_in: ate }])[0][1]}`;
  function baixarPdf() {
    try {
      const vis = F.filtrar(linhas, { ...filtro, coleta });
      paraPdf({
        titulo: 'Fichas de hóspedes (FNRH)', hotel: hotel(),
        subtitulo: `${periodoTxt()} · ${vis.length} hóspede(s) · gerado em ${new Date(agora()).toLocaleDateString('pt-BR')}`,
        colunas: F.COLUNAS_LISTA, linhas: F.linhasDaLista(vis, coleta),
        arquivo: nomeDoArquivo('fnrh', [slug(), de, ate], 'pdf'),
      });
    } catch (e) { aviso(e.message, 'erro'); }
  }
  function baixarPlanilha() {
    try {
      paraPlanilha({
        aba: 'FNRH', colunas: F.COLUNAS_PLANILHA, linhas: F.linhasDaPlanilha(F.filtrar(linhas, { ...filtro, coleta }), fuso(), coleta),
        arquivo: nomeDoArquivo('fnrh', [slug(), de, ate], 'xlsx'),
      });
    } catch (e) { aviso(e.message, 'erro'); }
  }
  function pdfDaFicha(l) {
    try {
      const corpo = F.fichaDePessoa(l, hoje(), fuso()).flatMap(([titulo, pares]) => [[titulo.toUpperCase(), ''], ...pares]);
      paraPdf({
        titulo: `Ficha de hóspede — ${l.nome || ''}`, hotel: hotel(), orientacao: 'portrait',
        subtitulo: `Quarto ${l.quarto || ''} · gerada em ${new Date(agora()).toLocaleDateString('pt-BR')}`,
        colunas: ['Campo', 'Valor'], linhas: corpo,
        arquivo: nomeDoArquivo('fnrh', [slug(), l.quarto || 'quarto', l.check_in || ''], 'pdf'),
      });
    } catch (e) { aviso(e.message, 'erro'); }
  }

  refazer();
  return () => { vivo = false; };
}
